import { cardQuestion, type Edge, type Stage, STAGES, type Step, TERMINAL } from './tree'

export const NODE_W = 260
/**
 * Cards size to their own question, so a long one is never clipped. Connectors
 * attach ANCHOR below the card top rather than at its centre, which keeps the
 * trunk dead straight even though the cards differ in height.
 */
export const ANCHOR = 48
const MIN_NODE_H = 104
const LINE_H = 17
/** Deliberately under the true fit, so wrapping never costs us a line. */
const CHARS_PER_LINE = 31
/** Matches the gap-1 between message lines on the card. */
const PARAGRAPH_GAP = 4
/** Header row, the collected-with line, padding and gaps. */
const NODE_CHROME_H = 69
export const COL_GAP = 76
export const LANE_GAP = 28
export const PAD = 32
/** Vertical clearance between two answers that lead to the same question. */
export const PARALLEL_GAP = 34
/** Two labels closer than this are pushed apart. */
const MIN_LABEL_GAP = 34
/** Headroom above lane 0 for skip-ahead arcs. */
export const TOP = 84

export interface GraphNode {
  service: string
  step: Step | null
  stage: Stage | null
  /** 1-based stage position, for the badge. */
  stagePosition: number
  /** Branches that loop straight back to this same question. */
  selfLoops: Edge[]
  col: number
  lane: number
  x: number
  y: number
  h: number
}

export interface GraphEdge {
  from: string
  to: string
  label: string
  kind: Edge['kind']
  /** Points backwards in the flow — drawn dashed, routed under the nodes. */
  isBack: boolean
  /** Skips at least one column — drawn in the jump colour. */
  isJump: boolean
  path: string
  labelX: number
  labelY: number
}

export interface Graph {
  nodes: GraphNode[]
  edges: GraphEdge[]
  width: number
  height: number
  entry: string
}

const ENTRY = 'ADD_EMAIL'

interface RawEdge {
  from: string
  to: string
  label: string
  kind: Edge['kind']
}

/** Tall enough for every message on the card, from a conservative wrap estimate. */
function estimateHeight(step: Step | null) {
  if (!step) return MIN_NODE_H
  const paragraphs = cardQuestion(step)
  const lines = paragraphs.reduce(
    (total, text) => total + Math.max(1, Math.ceil(text.length / CHARS_PER_LINE)),
    0
  )
  const gaps = (paragraphs.length - 1) * PARAGRAPH_GAP
  return Math.max(MIN_NODE_H, NODE_CHROME_H + lines * LINE_H + gaps)
}

/** Every step, flattened out of its stage, in source order. */
function collectNodes() {
  const nodes = new Map<string, GraphNode>()
  STAGES.forEach((stage, stageIndex) => {
    for (const step of stage.steps) {
      nodes.set(step.service, {
        service: step.service,
        step,
        stage,
        stagePosition: stageIndex + 1,
        selfLoops: step.edges.filter((e) => e.to === step.service),
        col: 0,
        lane: 0,
        x: 0,
        y: 0,
        h: estimateHeight(step),
      })
    }
  })
  nodes.set(TERMINAL, {
    service: TERMINAL,
    step: null,
    stage: null,
    stagePosition: 0,
    selfLoops: [],
    col: 0,
    lane: 0,
    x: 0,
    y: 0,
    h: MIN_NODE_H,
  })
  return nodes
}

function collectEdges(nodes: Map<string, GraphNode>): RawEdge[] {
  const raw: RawEdge[] = []
  for (const node of nodes.values()) {
    if (!node.step) continue
    for (const edge of node.step.edges) {
      // Self-loops ride along on the node itself rather than as a drawn edge.
      if (edge.to === node.service) continue
      if (!nodes.has(edge.to)) continue
      raw.push({ from: node.service, to: edge.to, label: edge.label, kind: edge.kind })
    }
  }
  return raw
}

/** Edges that close a cycle, found by DFS — they can't take part in ranking. */
function findBackEdges(nodes: Map<string, GraphNode>, raw: RawEdge[]) {
  const out = new Map<string, RawEdge[]>()
  for (const e of raw) {
    const list = out.get(e.from) ?? []
    list.push(e)
    out.set(e.from, list)
  }

  const back = new Set<string>()
  const state = new Map<string, 'open' | 'done'>()
  const key = (e: RawEdge) => `${e.from}->${e.to}`

  const walk = (service: string) => {
    state.set(service, 'open')
    for (const e of out.get(service) ?? []) {
      const s = state.get(e.to)
      if (s === 'open') {
        back.add(key(e))
      } else if (s === undefined) {
        walk(e.to)
      }
    }
    state.set(service, 'done')
  }

  walk(ENTRY)
  // Steps only reachable behind a metadata gate are never walked from the entry.
  for (const service of nodes.keys()) {
    if (!state.has(service)) walk(service)
  }
  return { back, key }
}

/**
 * Longest-path ranking over the DAG, then a pass that pulls gated entry points
 * (in-degree zero, but not the flow's entry) up against the step they feed.
 */
function assignColumns(nodes: Map<string, GraphNode>, forward: RawEdge[]) {
  const out = new Map<string, RawEdge[]>()
  const indeg = new Map<string, number>()
  for (const service of nodes.keys()) indeg.set(service, 0)
  for (const e of forward) {
    const list = out.get(e.from) ?? []
    list.push(e)
    out.set(e.from, list)
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1)
  }

  const rank = new Map<string, number>()
  const queue: string[] = []
  for (const [service, deg] of indeg) {
    if (deg === 0) {
      rank.set(service, 0)
      queue.push(service)
    }
  }

  while (queue.length) {
    const service = queue.shift() as string
    for (const e of out.get(service) ?? []) {
      rank.set(e.to, Math.max(rank.get(e.to) ?? 0, (rank.get(service) ?? 0) + 1))
      const deg = (indeg.get(e.to) ?? 0) - 1
      indeg.set(e.to, deg)
      if (deg === 0) queue.push(e.to)
    }
  }

  for (const [service, deg] of indeg) {
    // Guard only: back-edges are stripped, so nothing should be left unprocessed.
    if (deg > 0 && !rank.has(service)) rank.set(service, 0)
  }

  for (const service of nodes.keys()) {
    const isSource = forward.every((e) => e.to !== service)
    if (!isSource || service === ENTRY) continue
    const successors = (out.get(service) ?? []).map((e) => rank.get(e.to) ?? 0)
    if (successors.length) rank.set(service, Math.max(0, Math.min(...successors) - 1))
  }

  return rank
}

/**
 * Lanes by longest-path decomposition. The trunk is the flow's genuine longest
 * path, so it runs dead straight across lane 0; every remaining step is pulled
 * out as a side path that leaves the trunk and visibly rejoins wherever it lands
 * on an already-placed step. Side paths are then packed into the fewest rows
 * whose column spans don't collide.
 */
function assignLanes(nodes: Map<string, GraphNode>, forward: RawEdge[]) {
  const out = new Map<string, RawEdge[]>()
  for (const e of forward) {
    out.set(e.from, [...(out.get(e.from) ?? []), e])
  }

  const heights = new Map<string, number>()
  const heightOf = (service: string): number => {
    const cached = heights.get(service)
    if (cached !== undefined) return cached
    // Seed before recursing: forward edges are acyclic, this just bounds re-entry.
    heights.set(service, 0)
    const successors = out.get(service) ?? []
    const h = successors.length ? 1 + Math.max(...successors.map((e) => heightOf(e.to))) : 0
    heights.set(service, h)
    return h
  }
  for (const service of nodes.keys()) heightOf(service)

  const branch = new Map<string, number>()
  let nextBranch = 0
  const extractPath = (start: string) => {
    const b = nextBranch++
    let current: string | undefined = start
    while (current && !branch.has(current)) {
      branch.set(current, b)
      const onward = (out.get(current) ?? [])
        .map((e) => e.to)
        .filter((t) => !branch.has(t))
        .sort((a, c) => (heights.get(c) ?? 0) - (heights.get(a) ?? 0))
      current = onward[0]
    }
  }

  extractPath(ENTRY)
  for (const node of [...nodes.values()].sort((a, b) => a.col - b.col)) {
    if (!branch.has(node.service)) extractPath(node.service)
  }

  const span = new Map<number, { min: number; max: number }>()
  for (const node of nodes.values()) {
    const b = branch.get(node.service) as number
    const current = span.get(b)
    span.set(b, {
      min: Math.min(current?.min ?? node.col, node.col),
      max: Math.max(current?.max ?? node.col, node.col),
    })
  }

  // Row 0 is the trunk's alone; side paths first-fit into the rows below it.
  const rows: { min: number; max: number }[][] = [[span.get(0) as { min: number; max: number }]]
  const rowOf = new Map<number, number>([[0, 0]])
  const sidePaths = [...span.entries()]
    .filter(([b]) => b !== 0)
    .sort((a, c) => a[1].min - c[1].min || a[0] - c[0])
  for (const [b, s] of sidePaths) {
    let placed = rows.findIndex((row, i) => i > 0 && row.every((r) => s.max < r.min - 1 || s.min > r.max + 1))
    if (placed === -1) {
      rows.push([])
      placed = rows.length - 1
    }
    rows[placed].push(s)
    rowOf.set(b, placed)
  }

  const lane = new Map<string, number>()
  for (const node of nodes.values()) {
    lane.set(node.service, rowOf.get(branch.get(node.service) as number) ?? 0)
  }
  return lane
}

/**
 * Straight-ish hop between neighbouring steps. `spread` bows the middle of the
 * curve so two answers leading to the same question don't sit on one line — a
 * cubic's midpoint moves 0.75 of its control-point offset, hence the 4/3.
 */
function stepPath(from: GraphNode, to: GraphNode, spread: number) {
  const x1 = from.x + NODE_W
  const y1 = from.y + ANCHOR
  const x2 = to.x
  const y2 = to.y + ANCHOR
  const dx = Math.max(30, (x2 - x1) / 2)
  const bow = (spread * 4) / 3
  return `M ${x1} ${y1} C ${x1 + dx} ${y1 + bow}, ${x2 - dx} ${y2 + bow}, ${x2} ${y2}`
}

/**
 * A skip-ahead arcs over the top of the steps it passes. Longer skips — and
 * parallel ones — ride higher so overlapping arcs stay tellable apart.
 */
function jumpPath(from: GraphNode, to: GraphNode, stack: number) {
  const x1 = from.x + NODE_W / 2
  const y1 = from.y
  const x2 = to.x + NODE_W / 2
  const y2 = to.y
  const lift = jumpLift(from, to, stack)
  return `M ${x1} ${y1} C ${x1} ${lift}, ${x2} ${lift}, ${x2} ${y2}`
}

function jumpLift(from: GraphNode, to: GraphNode, stack: number) {
  const base = Math.min(from.y, to.y) - 14 - Math.min(to.col - from.col, 7) * 7
  return Math.max(10, base - stack)
}

/** Where a cubic with both control points at `ctrl` actually peaks. */
function arcMid(y1: number, y2: number, ctrl: number) {
  return (y1 + y2) / 8 + 0.75 * ctrl
}

/** A re-ask or a step back loops beneath the lanes. */
function backPath(from: GraphNode, to: GraphNode, floor: number, stack: number) {
  const x1 = from.x + NODE_W / 2
  const y1 = from.y + from.h
  const x2 = to.x + NODE_W / 2
  const y2 = to.y + to.h
  const drop = backDrop(from, to, floor, stack)
  return `M ${x1} ${y1} C ${x1} ${drop}, ${x2} ${drop}, ${x2} ${y2}`
}

function backDrop(from: GraphNode, to: GraphNode, floor: number, stack: number) {
  return floor + 14 + Math.min(Math.abs(from.col - to.col), 7) * 7 + stack
}

/** Last resort: nudge labels sharing a column apart so none is hidden. */
function spaceLabels(edges: GraphEdge[]) {
  const columns = new Map<number, GraphEdge[]>()
  for (const e of edges) {
    const k = Math.round(e.labelX / 80)
    columns.set(k, [...(columns.get(k) ?? []), e])
  }
  for (const group of columns.values()) {
    group.sort((a, b) => a.labelY - b.labelY)
    for (let i = 1; i < group.length; i++) {
      const gap = group[i].labelY - group[i - 1].labelY
      if (gap < MIN_LABEL_GAP) group[i].labelY = group[i - 1].labelY + MIN_LABEL_GAP
    }
  }
}

export function buildGraph(): Graph {
  const nodes = collectNodes()
  const raw = collectEdges(nodes)
  const { back, key } = findBackEdges(nodes, raw)
  const forward = raw.filter((e) => !back.has(key(e)))
  const rank = assignColumns(nodes, forward)

  for (const node of nodes.values()) {
    node.col = rank.get(node.service) ?? 0
  }

  const lane = assignLanes(nodes, forward)
  for (const node of nodes.values()) {
    node.lane = lane.get(node.service) ?? 0
  }

  const lanes = Math.max(...[...nodes.values()].map((n) => n.lane)) + 1
  const laneHeights = Array.from({ length: lanes }, (_, i) =>
    Math.max(...[...nodes.values()].filter((n) => n.lane === i).map((n) => n.h))
  )
  const laneTops: number[] = []
  for (let i = 0; i < lanes; i++) {
    laneTops.push(i === 0 ? TOP : laneTops[i - 1] + laneHeights[i - 1] + LANE_GAP)
  }

  for (const node of nodes.values()) {
    node.x = PAD + node.col * (NODE_W + COL_GAP)
    node.y = laneTops[node.lane]
  }

  const cols = Math.max(...[...nodes.values()].map((n) => n.col)) + 1
  const width = PAD * 2 + cols * NODE_W + (cols - 1) * COL_GAP
  const floor = laneTops[lanes - 1] + laneHeights[lanes - 1]

  // Several answers can lead to the same question; fan them so neither the
  // lines nor their labels stack.
  const parallelCount = new Map<string, number>()
  for (const e of raw) {
    parallelCount.set(key(e), (parallelCount.get(key(e)) ?? 0) + 1)
  }
  const parallelSeen = new Map<string, number>()

  const edges: GraphEdge[] = raw.map((e) => {
    const from = nodes.get(e.from) as GraphNode
    const to = nodes.get(e.to) as GraphNode
    const total = parallelCount.get(key(e)) ?? 1
    const index = parallelSeen.get(key(e)) ?? 0
    parallelSeen.set(key(e), index + 1)
    const spread = (index - (total - 1) / 2) * PARALLEL_GAP
    // Arcs stack outward instead of splitting either side of a line.
    const stack = index * PARALLEL_GAP + from.lane * 12

    const isBack = back.has(key(e))
    const isJump = !isBack && to.col - from.col > 1
    const path = isBack
      ? backPath(from, to, floor, stack)
      : isJump
        ? jumpPath(from, to, stack)
        : stepPath(from, to, spread)
    return {
      from: e.from,
      to: e.to,
      label: e.label,
      kind: e.kind,
      isBack,
      isJump,
      path,
      labelX: (from.x + to.x) / 2 + NODE_W / 2,
      // Curve midpoints, so a label sits on its own line rather than near it.
      labelY: isBack
        ? arcMid(from.y + from.h, to.y + to.h, backDrop(from, to, floor, stack))
        : isJump
          ? arcMid(from.y, to.y, jumpLift(from, to, stack))
          : (from.y + to.y) / 2 + ANCHOR + spread,
    }
  })

  spaceLabels(edges)

  return {
    nodes: [...nodes.values()],
    edges,
    width,
    // Room for the deepest re-ask loop under the last lane.
    height: floor + 90,
    entry: ENTRY,
  }
}
